/** The console uses the same Noctant login as the trading app. */

import { Show, createSignal, onMount } from "solid-js";
import { useNavigate } from "@solidjs/router";

import { ApiError, failureSentence } from "../lib/api";
import { renew, requestOwnProof, signIn } from "../lib/noctantLogin";
import { useSession } from "../lib/session";

export default function LoginPage() {
  const navigate = useNavigate();
  const session = useSession();
  const [email, setEmail] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [renewing, setRenewing] = createSignal(true);
  const [submitting, setSubmitting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  // The login's address is not confirmed: the page offers a new link to it.
  const [unproven, setUnproven] = createSignal(false);
  const [sendingLink, setSendingLink] = createSignal(false);
  const [linkAnswer, setLinkAnswer] = createSignal<string | null>(null);
  // The address this page signed in with: the link goes to it, whatever the
  // field says later. A page-load sign-in does not know it.
  let signedInAs: string | null = null;

  function signedIn() {
    session.refresh();
    navigate("/", { replace: true });
  }

  function refused(failure: unknown) {
    setError(failureSentence(failure));
    setUnproven(failure instanceof ApiError && failure.code === "unproven");
  }

  onMount(() => {
    void renew().then(signedIn, (failure: unknown) => {
      if (!(failure instanceof ApiError && failure.isUnauthorized)) refused(failure);
    }).finally(() => setRenewing(false));
  });

  async function onSubmit(event: Event) {
    event.preventDefault();
    if (submitting()) return;
    setSubmitting(true);
    setError(null);
    setUnproven(false);
    setLinkAnswer(null);
    signedInAs = email();
    try {
      await signIn(signedInAs, password());
      setPassword("");
      signedIn();
    } catch (failure) {
      refused(failure);
    } finally {
      setSubmitting(false);
    }
  }

  async function sendLink() {
    setSendingLink(true);
    const outcome = await requestOwnProof();
    setLinkAnswer({
      sent: `We've sent a link to ${signedInAs || "your address"}. Open it to confirm the address, then sign in here again. It works for 1 hour; you can ask for another one in 5 minutes.`,
      confirmed: "This address is already confirmed. Sign in again.",
      "signed-out": "Sign in again to ask for a new link.",
      unavailable: "We couldn't send the link right now. Try again in a few minutes.",
    }[outcome]);
    setSendingLink(false);
  }

  return (
    <div class="min-h-screen grid place-items-center px-4 bg-slate-50">
      <div class="card w-full max-w-sm">
        <div class="card-header">
          <h1 class="text-lg font-semibold">Noctant admin</h1>
        </div>
        <div class="card-body space-y-4">
          <Show when={!renewing()} fallback={<p class="text-sm text-slate-700">Signing you in…</p>}>
            <form class="space-y-3" onSubmit={onSubmit}>
              <div>
                <label class="label" for="email">Email</label>
                <input
                  id="email"
                  class="input"
                  type="email"
                  required
                  autocomplete="email"
                  placeholder="you@example.com"
                  value={email()}
                  onInput={(event) => setEmail(event.currentTarget.value)}
                />
              </div>
              <div>
                <label class="label" for="password">Password</label>
                <input
                  id="password"
                  class="input"
                  type="password"
                  required
                  autocomplete="current-password"
                  value={password()}
                  onInput={(event) => setPassword(event.currentTarget.value)}
                />
              </div>
              <Show when={error()}>
                <p class="text-sm text-red-600" role="alert">{error()}</p>
              </Show>
              <Show when={unproven()}>
                <button
                  type="button"
                  class="btn-secondary w-full"
                  disabled={sendingLink()}
                  onClick={() => void sendLink()}
                >
                  {sendingLink() ? "Sending…" : "Send me a new link"}
                </button>
                <Show when={linkAnswer()}>
                  {(said) => <p class="text-sm text-slate-700" role="status">{said()}</p>}
                </Show>
              </Show>
              <button
                type="submit"
                class="btn-primary w-full"
                disabled={submitting() || email() === "" || password() === ""}
              >
                {submitting() ? "Signing in…" : "Sign in"}
              </button>
            </form>
          </Show>
        </div>
      </div>
    </div>
  );
}
